import axios from "axios";

import { httpClient } from "../../../shared/api/httpClient";
import { env } from "../../../shared/config/env";
import { normalizeApiError } from "../../../shared/errors/normalizeApiError";
import type {
  AccessTokenResponse,
  AuthUser,
  LoginRequest,
  OAuthProvider,
} from "../model/types";

const authClient = axios.create({
  baseURL: env.apiUrl,
  timeout: env.apiTimeoutMs,
  withCredentials: true,
  headers: { Accept: "application/json" },
});

const normalizeFailure = (error: unknown): never => {
  throw normalizeApiError(error);
};

export const authApi = {
  oauthStartUrl(provider: OAuthProvider, returnTo: string): string {
    const normalizedReturnTo = returnTo.startsWith("/") ? returnTo : "/home";
    const path = `${env.apiUrl}/auth/oauth/${provider}/start`;
    const url = new URL(path, window.location.origin);
    url.searchParams.set("return_to", normalizedReturnTo);
    url.searchParams.set("app_type", env.appType);
    return url.toString();
  },

  async login(payload: LoginRequest): Promise<AccessTokenResponse> {
    try {
      const response = await authClient.post<AccessTokenResponse>("/auth/login", {
        ...payload,
        app_type: env.appType,
      });
      return response.data;
    } catch (error) {
      return normalizeFailure(error);
    }
  },

  async refresh(): Promise<AccessTokenResponse> {
    try {
      const response = await authClient.post<AccessTokenResponse>("/auth/refresh", null, {
        params: { app_type: env.appType },
      });
      return response.data;
    } catch (error) {
      return normalizeFailure(error);
    }
  },

  async logout(): Promise<void> {
    try {
      await authClient.post("/auth/logout", null, { params: { app_type: env.appType } });
    } catch (error) {
      return normalizeFailure(error);
    }
  },

  async getMe(): Promise<AuthUser> {
    const response = await httpClient.get<AuthUser>("/auth/me", {
      params: { app_type: env.appType },
    });
    return response.data;
  },
};
